import { Injectable } from '@angular/core';
//import { BlockProperties, BlockyJson } from './blockly-types.types';
import * as Blockly from 'blockly';
import { javascriptGenerator } from 'blockly/javascript';
import { BlockSearchService } from 'src/app/shared/services/block-search.service';
import { BlockProperties, BlockyJson, HiddenFieldDef } from './blocky-types.types';
import './field-icon';
import './field-chevron';
import './field-json-modal';
import { registerHiddenFieldsExtension } from './hidden-fields-extension';
import { registerBlockTooltip, registerDeprecatedBlock } from './blockly-toolbox-search-de';
import { registerTextJoinPlusMinus, registerCardButtonsNew, registerSkillForNextRunNew, registerButtonOutputNew, registerAnswerOptionsNew, registerOverlayTextBtnNew, registerOverlayPromptNew, registerOverlayClose, registerToastrPreconfNew, registerDocSearchNewV2 } from './text-join-plus-minus';
import { registerAiImageGenerateDetailed } from './blockly-aiimage-mutator';
@Injectable({
  providedIn: 'root'
})
export class BlocklyParserService {
  constructor(private blockSearch: BlockSearchService) {
    registerTextJoinPlusMinus();
    registerCardButtonsNew();
    registerSkillForNextRunNew();
    registerButtonOutputNew();
    registerAnswerOptionsNew();
    registerOverlayTextBtnNew();
    registerOverlayPromptNew();
    registerOverlayClose();
    registerToastrPreconfNew();
    registerDocSearchNewV2();
    registerAiImageGenerateDetailed();
    javascriptGenerator.forBlock['text_join'] = function (block) {
      const elements: string[] = [];
      let i = 0;
      while (block.getInput('ADD' + i)) {
        const child = block.getInputTargetBlock('ADD' + i);
        let notAtuple = javascriptGenerator.blockToCode(child);
        if (!notAtuple) {
          notAtuple = '""';
        }
        //@ts-ignore
        elements.push(notAtuple);
        i++;
      }
      const code = elements.join(' + ');
      return code;
    };
  }

  public defineBlock(template: string, blockType: string, blockProperties: BlockProperties = {}) {
    //    blockProperties.icon = 'ri-robot-line'

    // Skip re-registration if already registered (prevents "overwrites previous definition" warnings)
    if (Blockly.Blocks[blockType]) {
      return;
    }

    const blockJson = this._parse(template, blockType, blockProperties);
    if (!blockProperties.deprecated) {
      this.blockSearch.register({ type: blockType, json: blockJson, tooltip: blockProperties.tooltip });
      if (blockProperties.tooltip) {
        registerBlockTooltip(blockType, blockProperties.tooltip);
      }
    } else {
      registerDeprecatedBlock(blockType);
    }

    // Register hidden fields extension if this block uses it
    if (blockJson._hiddenFieldDefs && blockJson._hiddenFieldDefs.length > 0) {
      registerHiddenFieldsExtension();
    }

    Blockly.defineBlocksWithJsonArray([blockJson]);

    // Store hidden field definitions on the block prototype for the extension to use
    if (blockJson._hiddenFieldDefs && blockJson._hiddenFieldDefs.length > 0) {
      const originalInit = Blockly.Blocks[blockType].init;
      const hiddenDefs = blockJson._hiddenFieldDefs;
      Blockly.Blocks[blockType].init = function (this: any) {
        originalInit.call(this);
        this._hiddenFieldDefs = hiddenDefs;
        if (blockProperties.hat) {
          this.hat = blockProperties.hat;
        }
        // Render initial chevron (updateShape_ is only auto-called on loadExtraState)
        if (this.updateShape_) {
          this.updateShape_();
        }
      };
    } else if (blockProperties.hat) {
      // Hat-Property muss direkt auf dem Block gesetzt werden,
      // da jsonInit es nur aus style.hat liest, nicht aus dem JSON top-level
      const originalInit = Blockly.Blocks[blockType].init;
      Blockly.Blocks[blockType].init = function (this: Blockly.Block) {
        originalInit.call(this);
        this.hat = blockProperties.hat;
      };
    }
  }

  public patchBlockColour(blockType: string, colour: string | number) {
    const block = (Blockly.Blocks as any)[blockType];
    if (!block || !block.init) return;
    const originalInit = block.init;
    (Blockly.Blocks as any)[blockType] = {
      ...block,
      init: function (this: any) {
        originalInit.call(this);
        this.setColour(colour);
      }
    };
  }

  public defineJs(codeTemplate: string, blockType: string) {
    javascriptGenerator.forBlock[blockType] = (block) => {
      let generatedCode = codeTemplate || '// NO CODE TEMPLATE PROVIDED';
      const matches = [...generatedCode.matchAll(/{{(.*?)}}/g)];
      for (const match of matches) {
        //@ts-ignore
        const name = match[1].trim();
        const nameWithoutQuotes = name.replaceAll("!", "");
        let replacement = `"???"`;
        if (block.getField(nameWithoutQuotes)) {
          const raw = block.getFieldValue(nameWithoutQuotes);
          const field = block.getField(nameWithoutQuotes);
          if (field instanceof Blockly.FieldDropdown) {
            //ES IST EIN DROPDOWN
            replacement = raw;
          } else {
            // Checkbox-String zu echtem Boolean
            if (raw === 'TRUE') {
              replacement = 'true';
            } else if (raw === 'FALSE') {
              replacement = 'false';
            } else if (("" + name).endsWith("!")) {
              replacement = "" + raw;
            } else if (("" + name).endsWith("@")) {
              replacement = "" + raw;
            } else {
              replacement = JSON.stringify(raw);
            }
          }
        } else {
          const valueBlock = block.getInputTargetBlock(name) || block.getInputTargetBlock('HIDDEN_' + nameWithoutQuotes);
          if (valueBlock) {
            const valueCode = javascriptGenerator.blockToCode(valueBlock);
            replacement = Array.isArray(valueCode) ? valueCode[0] : valueCode || '""';
          } else if ((block as any).getHiddenFieldValue) {
            // Hidden field: use stored/default value from the extension
            const hiddenValue = (block as any).getHiddenFieldValue(nameWithoutQuotes);
            if (hiddenValue !== undefined && hiddenValue !== '') {
              if (hiddenValue === 'TRUE' || hiddenValue === true) {
                replacement = 'true';
              } else if (hiddenValue === 'FALSE' || hiddenValue === false) {
                replacement = 'false';
              } else if (("" + name).endsWith("!") || ("" + name).endsWith("@")) {
                replacement = "" + hiddenValue;
              } else {
                replacement = JSON.stringify(hiddenValue);
              }
            } else {
              // hidden value input with nothing connected → empty string (not "???")
              const defs = (block as any)._hiddenFieldDefs;
              if (defs?.some((d: any) => d.name === nameWithoutQuotes && d.argDef?.type === 'input_value')) {
                replacement = '""';
              }
            }
          }
        }
        generatedCode = generatedCode.replace(match[0], replacement);
      }

      //return generatedCode + '\n';
      generatedCode = generatedCode.split("function(){").join("function(){\n");

      if (generatedCode.endsWith(");")) {
        return generatedCode + '\n';
      }

      //return [generatedCode, Order.ADDITION];
      return generatedCode;
    };
  }


  private _parse(
    template: string,
    blockType: string,
    blockProperties: BlockProperties = {}
  ): BlockyJson {
    const tooltip = blockProperties.tooltip ?? '';
    const helpUrl = blockProperties.helpUrl ?? '';
    const colour = blockProperties.colour ?? '';
    let output: string | null | undefined = undefined;
    const metaLines = template.split('\n').filter(line => line.startsWith('@'));
    for (const line of metaLines) {
      if (line.startsWith('@output:')) {
        output = line.split(':')[1]?.trim() ?? null;
      }
    }

    template = template.split('\n').filter(line => !line.startsWith('@')).join('\n');

    // Unterstützt: ?-Prefix für hidden fields, :mode optional, @check optional, default optional (beliebige Reihenfolge)
    const regex = /{{\s*(\??)(\w+)(?::(field|value|statement|text|textarea|json|number|checkbox|dropdown))?(?:=([^@{}[\]]+))?(?:\[@check[:=]([^\]]+)\])?(?:=([^@{}[\]]+))?\s*}}/g;

    let match;
    let cursor = 0;

    const parts: ({
      type: 'label' | 'field' | 'value' | 'statement' | 'dropdown',
      name?: string,
      fieldType?: string,
      options?: string[],
      defaultValue?: any,
      check?: string | string[],
      text: string,
      hidden?: boolean
    })[] = [];

    while ((match = regex.exec(template)) !== null) {
      if (match.index > cursor) {
        parts.push({ type: 'label', text: template.substring(cursor, match.index) });
      }
      //@ts-ignore
      const hiddenPrefix = match[1]; // '?' or ''
      const name = match[2]!.trim();
      let mode = match[3]?.trim();
      const default1 = match[4]?.trim();
      const checkRaw = match[5]?.trim();
      const default2 = match[6]?.trim();
      const defaultRaw = default2 ?? default1;
      //console.log('MATCH-> ', default1);
      const isHidden = hiddenPrefix === '?';

      // 🔧 Sonderfall: check ist angegeben, aber kein mode => automatisch value
      if (!mode && checkRaw) mode = 'value';

      if (!mode) {
        mode = 'value';
      }
      //  console.log("MODE!!!!", mode);
      if (mode === 'field' || ['text', 'textarea', 'json', 'number', 'checkbox'].includes(mode)) {
        // Felder
        //@ts-ignore
        let fieldType = mode === 'field' ? 'text' : mode;
        let defaultValue: any = '';

        if (defaultRaw !== undefined) {
          if (fieldType === 'checkbox') {
            defaultValue = defaultRaw.toLowerCase() === 'true';
          } else if (fieldType === 'number') {
            defaultValue = Number(defaultRaw);
          } else {
            defaultValue = defaultRaw;
          }
        }
        parts.push({ type: 'field', name, fieldType, defaultValue, text: '', hidden: isHidden });
      }
      else if (mode === 'dropdown') {
        const check = checkRaw ? checkRaw.split(',').map(x => x.trim()) : undefined;
        const defaultValue = defaultRaw !== undefined
          ? isNaN(Number(defaultRaw)) ? defaultRaw : Number(defaultRaw)
          : undefined;
        parts.push({ type: 'dropdown', name, check, defaultValue, text: '', hidden: isHidden });
      }

      else if (mode === 'value') {
        const check = checkRaw ? checkRaw.split(',').map(x => x.trim()) : "";
        const defaultValue = defaultRaw !== undefined
          ? isNaN(Number(defaultRaw)) ? defaultRaw : Number(defaultRaw)
          : undefined;
        parts.push({ type: 'value', name, check, defaultValue, text: '', hidden: isHidden });
      } else if (mode === 'statement') {
        const check = checkRaw ? checkRaw.split(',').map(x => x.trim()) : undefined;
        parts.push({ type: 'statement', name, check, text: '', hidden: isHidden });
      }
      cursor = regex.lastIndex;
    }

    if (cursor < template.length) {
      parts.push({ type: 'label', text: template.substring(cursor) });
    }

    // Post-process: mark label parts that directly precede a hidden field as hidden too.
    // This prevents orphaned labels (e.g. "Delay:") from showing without their field.
    for (let i = 0; i < parts.length - 1; i++) {
      const currentPart = parts[i]!;
      const nextPart = parts[i + 1]!;
      if (currentPart.type === 'label' && nextPart.hidden) {
        // Extract the label text that belongs to the hidden field (last line segment)
        const labelText = currentPart.text;
        const lastNewline = labelText.lastIndexOf('\n');
        if (lastNewline >= 0) {
          // Split: keep the part before \n as visible, mark the rest as hidden label
          const hiddenLabel = labelText.substring(lastNewline + 1).trim();
          currentPart.text = labelText.substring(0, lastNewline);
          // Store the hidden label for the field definition
          nextPart.text = hiddenLabel;
        } else {
          // Entire label is for the hidden field
          currentPart.hidden = true;
          nextPart.text = labelText.trim();
        }
      }
    }

    const messageParts: string[] = [];
    const args: any[] = [];
    const hiddenFieldDefs: HiddenFieldDef[] = [];

    for (const part of parts) {
      //console.log('PART', part);
      if (part.type === 'label') {
        if (!part.hidden) {
          messageParts.push(part.text);
        }
      } else if (part.hidden) {
        // Hidden field: collect definition but don't add to visible message/args
        let argDef: any;
        let defaultValue: any = part.defaultValue ?? '';

        if (part.type === 'field') {
          switch (part.fieldType) {
            case 'number':
              argDef = { type: 'field_number', name: part.name, value: part.defaultValue ?? 0 };
              defaultValue = part.defaultValue ?? 0;
              break;
            case 'checkbox':
              argDef = { type: 'field_checkbox', name: part.name, checked: part.defaultValue ?? false };
              defaultValue = part.defaultValue ?? false;
              break;
            case 'textarea':
              argDef = { type: 'field_multiline_modal', name: part.name, text: part.defaultValue ?? '' };
              break;
            case 'json':
              argDef = { type: 'field_json_modal', name: part.name, text: part.defaultValue ?? '{}' };
              defaultValue = part.defaultValue ?? '{}';
              break;
            default:
              argDef = { type: 'field_input', name: part.name, text: part.defaultValue ?? '' };
          }
        } else if (part.type === 'dropdown') {
          let opts: [string, string][] = [];
          if (part.defaultValue) {
            opts = part.defaultValue.split(",").map((item: string) => {
              if (item.split('|').length === 2) {
                return [item.split('|')[0], item.split('|')[1]];
              } else {
                return [item, item];
              }
            });
          } else {
            opts = [['???', '???']];
          }
          argDef = { type: 'field_dropdown', name: part.name, options: opts };
          defaultValue = opts[0]?.[1] ?? '';
        } else if (part.type === 'value') {
          argDef = { type: 'input_value', name: part.name, check: part.check || null };
        } else if (part.type === 'statement') {
          argDef = { type: 'input_statement', name: part.name, check: part.check || null };
        } else {
          argDef = { type: 'field_input', name: part.name, text: '' };
        }

        // Shadow is no longer hardcoded here – use withNested to configure shadows
        // for hidden value inputs, just like for normal value inputs.
        hiddenFieldDefs.push({ name: part.name!, argDef, defaultValue, label: part.text || part.name });
      } else {
        messageParts.push(`%${args.length + 1}`);
        if (part.type === 'field') {
          switch (part.fieldType) {
            case 'number':
              args.push({ type: 'field_number', name: part.name, value: part.defaultValue ?? 0 });
              break;

            case 'checkbox':
              args.push({ type: 'field_checkbox', name: part.name, checked: part.defaultValue ?? false });
              break;
            case 'textarea':
              args.push({ type: 'field_multiline_modal', name: part.name, text: part.defaultValue ?? '' });
              break;
            case 'json':
              args.push({ type: 'field_json_modal', name: part.name, text: part.defaultValue ?? '{}' });
              break;
            default:
              args.push({ type: 'field_input', name: part.name, text: part.defaultValue ?? '' });
          }
        }
        else if (part.type === 'dropdown') {

          let opts: [string, string][] = [];
          if (part.defaultValue) {
            //@ts-ignore
            opts = part.defaultValue.split(",").map((item) => {
              if (item.split('|').length === 2) {
                const label = item.split('|')[0];
                const value = item.split('|')[1];
                //@ts-ignore
                const INVISIBLE_SPACE = "​ ";
                //@ts-ignore
                return [label.split(" ").join(INVISIBLE_SPACE), value];
              } else {
                return [item, item];
              }
            });
          } else {
            opts = [['???', '???']];
          }
          //console.log(opts);
          args.push({ type: 'field_dropdown', name: part.name, options: opts });
        }
        else if (part.type === 'value') {
          args.push({
            type: 'input_value',
            name: part.name,
            check: part.check ?? 'String'
          });
        } else if (part.type === 'statement') {
          args.push({
            type: 'input_statement',
            name: part.name,
            check: part.check ?? null
          });
        }
      }
    }

    // Icon als erstes Element voranstellen
    if (blockProperties.icon) {
      args.unshift({ type: 'field_icon', name: 'ICON', icon: blockProperties.icon });
      messageParts.unshift('%1');
      // Alle bestehenden %N Referenzen um 1 erhöhen
      for (let i = 1; i < messageParts.length; i++) {
        const part = messageParts[i];
        if (!part) continue;
        const m = part.match(/%\d+/g);
        if (m) {
          for (const ref of m) {
            const num = parseInt(ref.substring(1), 10);
            messageParts[i] = messageParts[i]!.replace(ref, `%${num + 1}`);
          }
        }
      }
    }

    const json: BlockyJson = {
      type: blockType,
      message0: messageParts.join(' '),
      args0: args,
      colour,
      tooltip,
      helpUrl,
      inputsInline: true
    };

    if (output !== undefined) {
      json.output = output;
    } else {
      json.previousStatement = null;
      json.nextStatement = null;
    }
    if (blockProperties.connection == "top") {
      delete json.nextStatement;
    }
    if (blockProperties.connection == "bottom") {
      delete json.previousStatement;
    }
    if (blockProperties.connection == "none") {
      delete json.nextStatement;
      delete json.previousStatement;
    }

    if (blockProperties.previousStatement) {
      json.previousStatement = blockProperties.previousStatement;
      //      json.output = undefined; // Wenn previousStatement gesetzt ist, sollte output null sein
    }
    if (blockProperties.nextStatement) {
      json.nextStatement = blockProperties.nextStatement;
      //    json.output = undefined; // Wenn previousStatement gesetzt ist, sollte output null sein
    }
    //    console.log('BLOCK JSON OUTPUT', json.output);

    if (blockProperties.inputsInline) {
      json.inputsInline = true;
    } else {
      json.inputsInline = false;
    }

    if (blockProperties.colourSecondary) { json.colourSecondary = blockProperties.colourSecondary; }
    if (blockProperties.colourTertiary) { json.colourSecondary = blockProperties.colourTertiary; }
    if (blockProperties.hat) { json.hat = blockProperties.hat; }

    // Hidden fields: store definitions and set mutator extension
    if (hiddenFieldDefs.length > 0) {
      json._hiddenFieldDefs = hiddenFieldDefs;
      json.mutator = 'hidden_fields_toggle';
    }

    return json;
  }
}
